<template>
  <div class="template-modal">
    <div class="template-box">
      <h3>{{ title }}</h3>

      <!-- Template Title -->
      <div class="form-group">
        <label>Template Title</label>
        <input
          type="text"
          v-model="localForm.temp_title"
          placeholder="Enter template title"
        />
      </div>

      <!-- Trigger / Category -->
      <div class="form-group">
        <label>Trigger</label>
        <input
          type="text"
          v-model="localForm.temp_cat"
          placeholder="Enter trigger (e.g., Payment Success)"
        />
      </div>

      <!-- Variables & Editor -->
      <div class="form-group">
        <label>Description / Body</label>

        <!-- Variable Insert Dropdown -->
        <div class="variables-toolbar">
          <select v-model="selectedVar" @change="insertVariable">
            <option value="">Insert Variable...</option>
            <option v-for="v in variables" :key="v.value" :value="v.value">
              {{ v.label }}
            </option>
          </select>
        </div>

        <!-- Quill Rich Text Editor -->
        <div ref="editor" class="quill-editor"></div>
      </div>

      <!-- Actions -->
      <div class="template-actions">
        <button class="btn-secondary" @click="$emit('close')">Cancel</button>
        <button class="btn-primary" @click="submitForm">
          <i class="bx bx-save"></i> Save Template
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineEmits, defineProps, nextTick } from "vue";
import { useToast } from "vue-toastification";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const props = defineProps({
  title: String,
  form: Object,
  userId: String,
  tempType: String,
});
const emit = defineEmits(["close", "save"]);
const toast = useToast();

const editor = ref(null);
let quill = null;

const selectedVar = ref("");
const variables = [
  { label: "Client Name", value: "{{client_name}}" },
  { label: "Invoice Number", value: "{{invoice_number}}" },
  { label: "Invoice Total", value: "{{invoice_total}}" },
  { label: "Due Date", value: "{{due_date}}" },
  { label: "Business Name", value: "{{business_name}}" },
  { label: "User Name", value: "{{user_name}}" },
];

const localForm = ref({
  temp_id: "",
  temp_title: "",
  temp_cat: "",
  temp_desc: "",
  temp_user: props.userId,
  temp_type: props.tempType,
});

// 🧩 Prefill form when editing
watch(
  () => props.form,
  async (val) => {
    if (val && Object.keys(val).length > 0) {
      localForm.value = {
        ...localForm.value,
        ...val,
        temp_user: props.userId,
        temp_type: props.tempType,
      };
      await nextTick();
      if (quill && val.temp_desc) quill.root.innerHTML = val.temp_desc;
    }
  },
  { immediate: true, deep: true }
);

// 🧩 Initialize Quill Editor
onMounted(() => {
  quill = new Quill(editor.value, {
    theme: "snow",
    placeholder: "Write your message here...",
    modules: {
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    },
  });

  // Update localForm.temp_desc whenever content changes
  quill.on("text-change", () => {
    localForm.value.temp_desc = quill.root.innerHTML;
  });

  // Prefill existing HTML if editing
  if (localForm.value.temp_desc) {
    quill.root.innerHTML = localForm.value.temp_desc;
  }
});

// 🧩 Insert Variable into editor
const insertVariable = () => {
  if (!selectedVar.value) return;
  const cursorPos = quill.getSelection(true);
  quill.insertText(cursorPos.index, selectedVar.value + " ");
  quill.setSelection(cursorPos.index + selectedVar.value.length + 1);
  selectedVar.value = "";
};

// 🧩 Submit form
const submitForm = () => {
  if (!localForm.value.temp_title.trim()) {
    toast.warning("Template title required");
    return;
  }
  if (!localForm.value.temp_cat.trim()) {
    toast.warning("Template trigger required");
    return;
  }
  if (!localForm.value.temp_desc.trim()) {
    toast.warning("Template description required");
    return;
  }

  emit("save", { ...localForm.value });
};
</script>

<style scoped>
.template-modal {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4600;
}

.template-box {
  background: #fff;
  border-radius: 18px;
  padding: 1.8rem;
  width: 540px;
  max-width: 95%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.25s ease forwards;
}

.template-box h3 {
  margin-bottom: 1rem;
  font-weight: 700;
  color: #111827;
}

.form-group {
  margin-bottom: 1rem;
}
.form-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.3rem;
}

.variables-toolbar {
  margin-bottom: 0.4rem;
}
.variables-toolbar select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #f9fafb;
}

.quill-editor {
  height: 180px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
}
.quill-editor:focus-within {
  border-color: var(--primary, #111827);
}

.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1.2rem;
}

.btn-primary {
  background: var(--primary, #111827);
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary:hover {
  background: var(--primary-dark, #1E3A8A);
}

.btn-secondary {
  background: #f3f4f6;
  color: #111827;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
.template-modal {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4600;
}

.template-box {
  background: #fff;
  border-radius: 18px;
  padding: 1.8rem;
  width: 480px;
  max-width: 90%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.25s ease forwards;
}

.template-box h3 {
  margin-bottom: 1rem;
  font-weight: 700;
  color: #111827;
}

.form-group {
  margin-bottom: 1rem;
}
.form-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.3rem;
}
.form-group input,
.form-group textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 0.95rem;
  transition: border 0.3s ease;
}
.form-group input:focus,
.form-group textarea:focus {
  border-color: var(--primary, #111827);
  outline: none;
}
.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1rem;
}
.btn-primary {
  background: var(--primary, #111827);
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
}
.btn-primary:hover {
  background: var(--primary-dark, #1E3A8A);
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
